/**
 * Construction and validation for the MedDream hangingProtocolsV2 document.
 *
 * The shapes here are the contract for export: anything we author has to carry
 * exactly the key sets MedDream writes, or merging our protocols into its
 * settings file would produce entries it silently ignores. The KEYS_* constants
 * below were derived from the real export (9 groups / 68 categories / 76 panels
 * / 140 viewports) and are asserted against it in the round-trip test.
 */

import {
  ALIGNMENT,
  APPLY_MODE,
  DOCUMENT_VERSION,
  GROUP_KIND,
  LINK_SCROLL_SERIES,
  MPR_MIST,
  MPR_SERIES_IN_VIEWER,
  SCALE,
  SYNC_ACTIONS,
} from "./protocolEnums";

export const KEYS_DOCUMENT = [
  "applyConfiguration",
  "hp",
  "hpDrag",
  "hpComparison",
  "version",
];

export const KEYS_STANDARD_GROUP = [
  "id",
  "name",
  "creationDateTime",
  "description",
  "active",
  "devices",
  "groupConditions",
  "categories",
  "skipIfOtherGroupHasApplicableHp",
  "quickSaveModality",
];

/** hpDrag / hpComparison groups carry a reduced set — no conditions or devices. */
export const KEYS_SPECIAL_GROUP = [
  "id",
  "name",
  "creationDateTime",
  "description",
  "active",
  "categories",
];

export const KEYS_CATEGORY = [
  "id",
  "name",
  "description",
  "studyConditions",
  "linkScrollSeries",
  "syncActions",
  "panelsLayout",
  "openAllPriors",
  "openMaxPriors",
  "expandAllPriors",
  "openNewComparison",
  "priorStudyConditions",
  "otherStudyConditions",
];

/** Categories under hpDrag / hpComparison omit every prior-related field. */
export const KEYS_SPECIAL_CATEGORY = [
  "id",
  "name",
  "description",
  "studyConditions",
  "linkScrollSeries",
  "syncActions",
  "panelsLayout",
];

export const KEYS_PANEL = [
  "isOverview",
  "quadrantZoomEnabled",
  "comparisonStudyProtocolEnabled",
  "comparisonStudyProtocolGroup",
  "priorStudy",
  "otherStudy",
  "column",
  "row",
  "layout",
];

export const KEYS_VIEWPORT = [
  "overviewLayout",
  "adjustLayout",
  "mprMist",
  "priorStudy",
  "otherStudy",
  "column",
  "row",
  "seriesConditions",
  "petSeriesConditions",
  "instanceConditions",
  "koInstance",
  "autoApplyPR",
  "windowing",
  "scale",
  "flipVertically",
  "flipHorizontally",
  "alignment",
  "rotation",
  "customScaleValue",
  "mprSeriesInViewer",
];

export const KEYS_RULE = [
  "id",
  "field",
  "operator",
  "value",
  "valueSource",
  "path",
  "inputType",
];

export const KEYS_RULE_GROUP = ["id", "not", "rules"];

/* ------------------------------------------------------------------- ids */

/** MedDream identifies groups and categories by epoch milliseconds, as a string. */
export function newEntityId() {
  return String(Date.now());
}

export function newNodeId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function nowIso() {
  return new Date().toISOString();
}

/* -------------------------------------------------------------- factories */

/**
 * An unset condition block. MedDream writes `id: ""` for slots that were never
 * touched (koInstance, autoApplyPR, petSeriesConditions), so that is the
 * default here rather than a fresh uuid.
 */
export function createEmptyRuleGroup(id = "") {
  return { id, not: false, rules: [] };
}

export function createRule({
  field,
  operator,
  value = "",
  inputType = "string",
  path = [],
  id = newNodeId(),
}) {
  return { id, field, operator, value: String(value), valueSource: "value", path: [...path], inputType };
}

/**
 * The instance condition a new viewport starts with: the series' first image.
 *
 * MedDream's two "empty condition" cases are not symmetric. Its
 * `getValidSeries` returns a `"searchFromAll"` sentinel when `rules` is empty, so
 * an unset SERIES condition means *any* series. Its `getValidInstance` has no
 * such sentinel and returns `false`, so an unset INSTANCE condition means *no*
 * instance: it walks every image of every candidate series, matches none, and
 * MedDream deletes the viewport container. The cell hangs nothing at all, even
 * though its series condition was perfectly satisfiable.
 *
 * MedDream's own editor never writes that shape — across the reference export's
 * 140 viewports, 130 carry both conditions and 10 carry neither; not one carries
 * a series condition alone. `instance_number = 1` is its commonest default (55 of
 * those 140), so that is what we author too. See {@link isDeadViewport} for the
 * check that catches documents predating this default.
 */
export function createFirstInstanceConditions() {
  return {
    id: newNodeId(),
    not: false,
    rules: [createRule({ field: "instance_number", operator: "=", value: 1, inputType: "numeric" })],
  };
}

/**
 * Whether an instance condition is exactly what {@link createFirstInstanceConditions}
 * writes.
 *
 * The grid summary hides it. It is on every new viewport, so counting it would
 * label an untouched cell as configured and bury the cells the author actually
 * set up — the one thing the grid exists to show.
 */
export function isDefaultInstanceConditions(group) {
  const rules = group?.rules ?? [];
  if (rules.length !== 1) return false;
  const rule = rules[0];
  return rule?.field === "instance_number" && rule?.operator === "=" && String(rule?.value) === "1";
}

export function createViewport({ row = 1, column = 1 } = {}) {
  return {
    overviewLayout: { column: 1, row: 1 },
    adjustLayout: false,
    mprMist: MPR_MIST.DISABLED,
    priorStudy: null,
    otherStudy: null,
    column,
    row,
    seriesConditions: createEmptyRuleGroup(newNodeId()),
    petSeriesConditions: createEmptyRuleGroup(),
    instanceConditions: createFirstInstanceConditions(),
    koInstance: createEmptyRuleGroup(),
    autoApplyPR: createEmptyRuleGroup(),
    windowing: null,
    scale: SCALE.FIT_TO_SCREEN,
    flipVertically: false,
    flipHorizontally: false,
    alignment: ALIGNMENT.CENTER,
    rotation: 0,
    customScaleValue: 1,
    mprSeriesInViewer: MPR_SERIES_IN_VIEWER.NO,
  };
}

export function createPanel({ row = 1, column = 1, rows = 1, columns = 1 } = {}) {
  const viewports = [];
  for (let r = 1; r <= rows; r += 1) {
    for (let c = 1; c <= columns; c += 1) viewports.push(createViewport({ row: r, column: c }));
  }
  return {
    isOverview: false,
    quadrantZoomEnabled: false,
    comparisonStudyProtocolEnabled: false,
    comparisonStudyProtocolGroup: null,
    priorStudy: null,
    otherStudy: null,
    column,
    row,
    layout: { columns, rows, viewports },
  };
}

export function createCategory({
  name = "",
  description = "",
  rows = 1,
  columns = 1,
  kind = GROUP_KIND.STANDARD,
} = {}) {
  const base = {
    id: newEntityId(),
    name,
    description,
    studyConditions: createEmptyRuleGroup(newNodeId()),
    linkScrollSeries: LINK_SCROLL_SERIES.OFF,
    syncActions: SYNC_ACTIONS.OFF,
    panelsLayout: { columns: 1, rows: 1, panels: [createPanel({ rows, columns })] },
  };
  if (kind !== GROUP_KIND.STANDARD) return base;
  return {
    ...base,
    openAllPriors: false,
    openMaxPriors: 0,
    expandAllPriors: false,
    openNewComparison: false,
    priorStudyConditions: [createEmptyRuleGroup(newNodeId())],
    otherStudyConditions: [],
  };
}

export function createGroup({
  name = "",
  description = "",
  kind = GROUP_KIND.STANDARD,
  active = true,
} = {}) {
  if (kind !== GROUP_KIND.STANDARD) {
    return {
      id: newEntityId(),
      name,
      creationDateTime: nowIso(),
      description,
      active,
      categories: [],
    };
  }
  // Written out in MedDream's own key order to keep merge diffs readable.
  return {
    id: newEntityId(),
    name,
    creationDateTime: nowIso(),
    description,
    active,
    devices: [],
    groupConditions: createEmptyRuleGroup(newNodeId()),
    categories: [],
    skipIfOtherGroupHasApplicableHp: false,
    quickSaveModality: null,
  };
}

export function createDocument() {
  return {
    applyConfiguration: {
      applyOnFirstStudyOpen: APPLY_MODE.AUTO,
      applyOnFirstStudyClose: APPLY_MODE.NO,
      applyOnStudiesChange: APPLY_MODE.MENU,
    },
    hp: [],
    hpDrag: [],
    hpComparison: [],
    version: DOCUMENT_VERSION,
  };
}

/* ------------------------------------------------------------- validation */

const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

function checkKeys(node, expected, path, issues) {
  if (!isPlainObject(node)) {
    issues.push({ path, message: "expected an object" });
    return;
  }
  const actual = Object.keys(node);
  const missing = expected.filter((k) => !actual.includes(k));
  const extra = actual.filter((k) => !expected.includes(k));
  if (missing.length) issues.push({ path, message: `missing keys: ${missing.join(", ")}` });
  if (extra.length) issues.push({ path, message: `unexpected keys: ${extra.join(", ")}` });
}

function validateRuleGroup(group, path, issues) {
  checkKeys(group, KEYS_RULE_GROUP, path, issues);
  if (!isPlainObject(group) || !Array.isArray(group.rules)) return;

  group.rules.forEach((entry, i) => {
    const at = `${path}.rules[${i}]`;
    const shouldBeSeparator = i % 2 === 1;

    if (shouldBeSeparator) {
      if (entry !== "and" && entry !== "or") {
        issues.push({ path: at, message: `expected "and" or "or" separator` });
      }
      return;
    }
    if (typeof entry === "string") {
      issues.push({ path: at, message: "expected a rule or nested group, got a separator" });
      return;
    }
    if (isPlainObject(entry) && Array.isArray(entry.rules)) {
      validateRuleGroup(entry, at, issues);
      return;
    }
    checkKeys(entry, KEYS_RULE, at, issues);
    if (isPlainObject(entry) && typeof entry.value !== "string") {
      // Numeric fields still store their value as a string in this format.
      issues.push({ path: `${at}.value`, message: "expected a string" });
    }
  });

  // A group toggles one combinator for all of its operands.
  const separators = new Set(group.rules.filter((r) => typeof r === "string"));
  if (separators.size > 1) {
    issues.push({ path, message: `mixed combinators: ${[...separators].join(", ")}` });
  }
}

function validateViewport(viewport, path, issues) {
  checkKeys(viewport, KEYS_VIEWPORT, path, issues);
  if (!isPlainObject(viewport)) return;
  ["seriesConditions", "petSeriesConditions", "instanceConditions", "koInstance", "autoApplyPR"].forEach(
    (slot) => {
      if (viewport[slot]) validateRuleGroup(viewport[slot], `${path}.${slot}`, issues);
    }
  );
}

function validatePanel(panel, path, issues) {
  checkKeys(panel, KEYS_PANEL, path, issues);
  if (!isPlainObject(panel) || !isPlainObject(panel.layout)) return;
  (panel.layout.viewports || []).forEach((viewport, i) =>
    validateViewport(viewport, `${path}.layout.viewports[${i}]`, issues)
  );
}

function validateCategory(category, path, issues, kind) {
  checkKeys(
    category,
    kind === GROUP_KIND.STANDARD ? KEYS_CATEGORY : KEYS_SPECIAL_CATEGORY,
    path,
    issues
  );
  if (!isPlainObject(category)) return;

  if (category.studyConditions) {
    validateRuleGroup(category.studyConditions, `${path}.studyConditions`, issues);
  }
  ["priorStudyConditions", "otherStudyConditions"].forEach((slot) => {
    (category[slot] || []).forEach((tree, i) =>
      validateRuleGroup(tree, `${path}.${slot}[${i}]`, issues)
    );
  });
  if (isPlainObject(category.panelsLayout)) {
    (category.panelsLayout.panels || []).forEach((panel, i) =>
      validatePanel(panel, `${path}.panelsLayout.panels[${i}]`, issues)
    );
  }
}

function validateGroup(group, path, issues, kind) {
  checkKeys(group, kind === GROUP_KIND.STANDARD ? KEYS_STANDARD_GROUP : KEYS_SPECIAL_GROUP, path, issues);
  if (!isPlainObject(group)) return;
  if (kind === GROUP_KIND.STANDARD && group.groupConditions) {
    validateRuleGroup(group.groupConditions, `${path}.groupConditions`, issues);
  }
  (group.categories || []).forEach((category, i) =>
    validateCategory(category, `${path}.categories[${i}]`, issues, kind)
  );
}

/**
 * Structural check against the hangingProtocolsV2 contract.
 * Returns `{ valid, issues }` — issues carry a JSON path so a failure points at
 * the offending node rather than just the document.
 */
export function validateDocument(document) {
  const issues = [];
  checkKeys(document, KEYS_DOCUMENT, "$", issues);

  if (isPlainObject(document)) {
    Object.values(GROUP_KIND).forEach((kind) => {
      const groups = document[kind];
      if (!Array.isArray(groups)) {
        issues.push({ path: `$.${kind}`, message: "expected an array" });
        return;
      }
      groups.forEach((group, i) => validateGroup(group, `$.${kind}[${i}]`, issues, kind));
    });
  }

  return { valid: issues.length === 0, issues };
}

/** Every group across the three collections, tagged with which one it came from. */
export function listGroups(document) {
  return Object.values(GROUP_KIND).flatMap((kind) =>
    (document?.[kind] || []).map((group) => ({ kind, group }))
  );
}

/* ------------------------------------------------------------------- lint */

/**
 * A viewport that is well-formed but will hang nothing in the Advance Viewer:
 * it has a series condition and no instance condition.
 *
 * See {@link createFirstInstanceConditions} for why MedDream treats that shape
 * as "match no instance" rather than "show the first image". New viewports carry
 * a default instance condition, so this catches two cases: protocols authored
 * before that default existed, and ones where an author has since cleared the
 * field.
 */
export function isDeadViewport(viewport) {
  const seriesRules = viewport?.seriesConditions?.rules?.length ?? 0;
  const instanceRules = viewport?.instanceConditions?.rules?.length ?? 0;
  return seriesRules > 0 && instanceRules === 0;
}

export const DEAD_VIEWPORT_WARNING =
  "has a series condition but no instance condition — the Advance Viewer will hang nothing in it";

/**
 * Problems that are not structural: a document can carry every key MedDream
 * expects and still hang nothing.
 *
 * Deliberately separate from {@link validateDocument}. Delivery refuses to
 * publish a document that fails validation, and refusing over this would leave
 * the radiologist with no protocols in the Advance Viewer at all — strictly
 * worse than some blank viewports. So these are warnings to show and log, never
 * a gate.
 */
export function lintDocument(document) {
  const warnings = [];
  Object.values(GROUP_KIND).forEach((kind) => {
    (document?.[kind] || []).forEach((group, g) => {
      (group?.categories || []).forEach((category, c) => {
        (category?.panelsLayout?.panels || []).forEach((panel, p) => {
          (panel?.layout?.viewports || []).forEach((viewport, v) => {
            if (!isDeadViewport(viewport)) return;
            warnings.push({
              path: `$.${kind}[${g}].categories[${c}].panelsLayout.panels[${p}].layout.viewports[${v}]`,
              message: DEAD_VIEWPORT_WARNING,
            });
          });
        });
      });
    });
  });
  return warnings;
}
